import './index.scss';
import LogoColor from 'src/assets/svg/logo_color.svg?react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Col, Divider, Row, Spin } from 'antd';
import { IconChevronLeft } from '@tabler/icons-react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import axios, { AxiosError } from 'axios';
import Step1 from './step1';
import Step2 from './step2';
import Step3 from './step3';
import SingleStepRegistration from './single-step';

import { Text, Link } from 'src/shared/components/typography';
import { useMount } from 'ahooks';
import { useBackHandler } from 'src/common/use-back-handler';

export interface RegistrationFormValues {
  role: 'Person' | 'EnterpriseAdmin';
  email: string;
  // Opcional porque de verdad lo es: el estado arranca vacio y el checkbox no se marca hasta el paso 2,
  // asi que antes de eso no hay valor. Declarado como `boolean` a secas, el `?? false` de `onFinish` era
  // codigo muerto para TypeScript -- una rama que el tipo dice que no puede darse -- y cualquier limpieza
  // se lo habria llevado por redundante, devolviendo el mensaje equivocado a quien si marco la casilla.
  termsAccepted?: boolean;
  password: string;
}

/// El cuerpo que espera `AccountController.Register`. Existe para que el compilador vigile que
/// `termsAccepted` sale siempre como booleano: es lo que hace fiable la distincion que el servidor hace
/// entre un campo ausente (pantalla anterior a este cambio) y uno sin marcar.
interface RegisterRequest {
  email: string;
  password: string;
  role: 'Person' | 'EnterpriseAdmin';
  termsAccepted: boolean;
}

/// Codigos que manda el servidor cuando rechaza el alta por el consentimiento (`ConsentErrorCodes`).
const CONSENT_NOT_ACCEPTED = 'ConsentNotAccepted';
const CONSENT_FIELD_MISSING = 'ConsentFieldMissing';

export interface StepProps {
  data: Partial<RegistrationFormValues>;
  loading: boolean;
  onNext: (nextData?: Partial<RegistrationFormValues>) => void;
  onChange: (next: Partial<RegistrationFormValues>) => void;
  setLoading: (next: boolean) => void;
}

const IndexView: React.FC<any> = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const handleBack = useBackHandler();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<RegistrationFormValues>({} as RegistrationFormValues);
  const [errorMsg, setErrorMsg] = useState<string>();
  const [errorCode, setErrorCode] = useState<string>();
  const errorRef = useRef<HTMLDivElement>(null);

  // Solo cuando aparece un aviso nuevo. El aviso sale arriba del todo, y en movil o con el formulario ya
  // desplazado quedaba fuera de vista: el envio parecia no haber hecho nada.
  useEffect(() => {
    if ((errorMsg || '').trim().length > 0) {
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [errorMsg]);

  const [searchParams] = useSearchParams();
  const draftShortlistId = searchParams.get('sid');
  const emailParam = searchParams.get('email');
  const fromShortlist = searchParams.get('ref') === 'shortlist';
  const redirectUrl =
    `${window.REACT_APP_WEBSITE_BASE_URI}/default-user-page` + (draftShortlistId ? `?sid=${encodeURIComponent(draftShortlistId)}` : '');

  const steps = draftShortlistId ? [SingleStepRegistration] : [Step1, Step2, Step3];

  const { role } = useParams();

  useMount(() => {
    const onRoleSelected = (role: string | undefined) => {
      if (role === 'services') {
        setData((c) => ({ ...c, role: 'Person', email: emailParam ?? c.email }));
      } else if (role === 'enterprises' || draftShortlistId) {
        setData((c) => ({ ...c, role: 'EnterpriseAdmin', email: emailParam ?? c.email }));
      }
    };
    onRoleSelected(role);
  });

  useEffect(() => {
    if (emailParam) {
      setData((c) => ({ ...c, email: emailParam }));
    }
  }, [emailParam]);

  const { step: routeStep = '0' } = useParams();

  // El paso y el rol salen de la URL (`/registration/:role?/:step?`), asi que son entrada del usuario:
  // un enlace viejo, un rastreador o una errata llegan aqui. El paso se acotaba por abajo pero no por
  // arriba, y `steps[3]` es `undefined`, con lo que React recibia `<undefined />` y la pagina entera
  // moria con "Element type is invalid".
  //
  // La URL solo puede nombrar los indices 0 y 1, nunca el ultimo, y fuera de rango se vuelve al 0.
  // El motivo no es la robustez sino el consentimiento: el checkbox de condiciones de uso vive solo
  // en `step2.tsx` (indice 1) y `single-step.tsx`, `onFinish` no envia `termsAccepted` y
  // `RegisterInputModel` tampoco lo tiene, asi que no hay nada en servidor que lo respalde. Si la URL
  // pudiera nombrar el indice 2, `/registration/<rol>/2?email=alguien@x.com` caeria en `step3.tsx`
  // -- la pantalla de contrasena -- con el correo ya relleno, y desde ahi `nextStep` llama a
  // `onFinish` y da de alta: cuenta creada sin haber visto las condiciones. Al indice 2 se llega
  // avanzando, no enlazando.
  //
  // Capar en 1 no rompe a nadie: website enlaza `/registration/<rol>` y `/registration/<rol>/1` desde
  // 12 sitios y ninguno pasa de ahi, y la propia app navega a `/registration` a secas. Con `sid`,
  // `steps` tiene un solo elemento y el tope baja solo a 0.
  //
  // El rol se valida por lo mismo: `useMount` solo entiende `services` y `enterprises`, y con
  // cualquier otro valor `data.role` queda `undefined`. Eso pinta `step2.tsx` en variante de empresa
  // -- con la validacion que rechaza correos personales -- y falla al enviar por el `[Required]` de
  // `Role`, con un mensaje que habla del correo. Rol desconocido, vuelta a elegir perfil.
  //
  // El `?? 0` que habia aqui era codigo muerto: `parseInt` devuelve `NaN`, nunca null ni undefined,
  // asi que el `??` no disparaba jamas y quien hacia el trabajo era el `Number.isNaN` siguiente.
  const roleIsKnown = role === 'services' || role === 'enterprises' || !!draftShortlistId;
  const parsedStep = Number.parseInt(routeStep, 10);
  const maxStepFromUrl = Math.min(1, steps.length - 1);
  const stepIsInRange = !Number.isNaN(parsedStep) && parsedStep >= 0 && parsedStep <= maxStepFromUrl;

  const [step, setStep] = useState<number>(roleIsKnown && stepIsInRange ? parsedStep : 0);

  // El paso donde vive la casilla de condiciones: el segundo del alta normal, el unico que hay cuando se
  // entra desde una shortlist.
  const consentStep = draftShortlistId ? 0 : 1;

  const onFinish = async (data: RegistrationFormValues) => {
    // Se limpia al reenviar: si no, un segundo intento que falla por otra cosa -- o que va bien y tarda --
    // se mira con el aviso del intento anterior todavia en pantalla.
    setErrorMsg(undefined);
    setErrorCode(undefined);
    setIsLoading(true);
    try {
      // El payload va tipado y no inferido: `axios.post` toma el cuerpo como un generico que deduce del
      // objeto literal, asi que sin este tipo el `?? false` de abajo no lo exige nadie -- se podia quitar
      // y `tsc` seguia compilando, dejando toda la distincion entre "no marcado" y "pantalla vieja"
      // sostenida por un comentario. Con `termsAccepted: boolean` no opcional, quitarlo rompe el build.
      const payload: RegisterRequest = {
        email: data.email,
        password: data.password,
        role: data.role,
        // Hasta este cambio el checkbox de condiciones de uso no salia de esta pantalla: habilitaba el
        // boton y se perdia aqui.
        //
        // El `?? false` es lo que hace fiable la distincion que el servidor espera. Alli, un campo
        // ausente significa "esta pantalla es anterior al cambio y no conoce el campo" -- solo puede
        // pasar con un bundle viejo aun cargado en un navegador, en los minutos siguientes a un
        // despliegue -- y `false` significa "no lo marco". Esta pantalla siempre manda booleano, asi
        // que nunca se confunde con la otra: antd da `undefined` para un checkbox sin marcar, y sin
        // normalizar aqui ese caso llegaria como ausente y sacaria el mensaje equivocado.
        termsAccepted: data.termsAccepted ?? false
      };

      const result = await axios.post<{ message: string }>('/account/register', payload);
      if (result?.status === 200) {
        window.location.href = redirectUrl;
      } else {
        const msg = result?.data?.message ?? (result as any as AxiosError<{ message: string }>)?.response?.data?.message ?? t('Error inesperado');
        setErrorMsg(msg);
        setIsLoading(false);
      }
    } catch (e) {
      console.error('Registration', e);
      const axiosError = e as AxiosError<{ message: string; code?: string }>;
      const code = axiosError?.response?.data?.code;
      setErrorMsg(axiosError?.response?.data?.message?.toString() ?? (t('Error inesperado') as string));
      setErrorCode(code);

      // Si lo que falta es la casilla, devolver a donde esta: el aviso solo no sirve de nada estando en
      // el paso de la contrasena, porque lo que hay que arreglar no se ve desde ahi.
      if (code === CONSENT_NOT_ACCEPTED) {
        setStep(consentStep);
      }

      setIsLoading(false);
    }
  };

  const nextStep = (nextData?: Partial<RegistrationFormValues>) => {
    const merged = { ...data, ...(nextData || {}) } as RegistrationFormValues;

    // Se guarda siempre, tambien al enviar. Antes en el ultimo paso se pasaba a `onFinish` sin guardar, y
    // un rechazo del servidor que devuelve al paso de la casilla se habria llevado por delante el correo
    // y la contrasena que la persona acababa de escribir.
    setData(merged);

    if (step + 1 === steps.length) {
      onFinish(merged);
    } else {
      setStep(Math.min(step + 1, steps.length - 1));
    }
  };

  const prevStep = () => {
    // El aviso se va con el paso: si no, quien falla, retrocede y vuelve a avanzar sigue viendo el error
    // anterior como si acabara de pasar.
    setErrorMsg(undefined);
    setErrorCode(undefined);
    setStep((c) => Math.max(c - 1, 0));
  };

  const CurrentStep = steps[step];

  return (
    <div id="registration-container">
      <div id="logo-container">
        <span title="back-cta" role="img" id="back-cta" className={isLoading ? 'anticon disabled' : 'anticon'}>
          <IconChevronLeft
            onClick={
              isLoading
                ? undefined
                : () => {
                    if (step === 0) handleBack();
                    else prevStep();
                  }
            }
            size={20}
            strokeWidth={3}
            color={isLoading ? 'lightgrey' : '#22817a'}
          />
        </span>
        <LogoColor />
      </div>
      <div id="card-container">
        <Spin spinning={isLoading} size={'large'}>
          <Card>
            {(errorMsg || '').trim().length > 0 && (
              <Row style={{ marginBottom: 32 }}>
                <Col span={24}>
                  {/* El scroll lo hace un efecto y no un `ref` en linea: una flecha declarada aqui es una
                    funcion nueva en cada render, asi que React la volvia a invocar cada vez y el aviso se
                    reposicionaba solo con teclear en un campo. Ver `useEffect` sobre `errorMsg`. */}
                  <div ref={errorRef}>
                    <Alert
                      closable
                      showIcon
                      onClose={() => {
                        setErrorMsg(undefined);
                        setErrorCode(undefined);
                      }}
                      description={errorMsg}
                      type="error"
                      // Solo cuando recargar es de verdad lo que lo arregla: esta pantalla es anterior al
                      // cambio y no manda el campo, asi que reintentar desde aqui volveria a fallar igual.
                      action={
                        errorCode === CONSENT_FIELD_MISSING ? (
                          <Button size="small" type="primary" onClick={() => window.location.reload()}>
                            {t('Recargar')}
                          </Button>
                        ) : undefined
                      }
                    />
                  </div>
                </Col>
              </Row>
            )}
            <CurrentStep
              data={data}
              loading={isLoading}
              onChange={(v) => setData((c) => ({ ...c, ...v }))}
              onNext={nextStep}
              setLoading={setIsLoading}
            />
            {!draftShortlistId && !fromShortlist && (
              <>
                <Divider style={{ marginTop: 40, marginBottom: 35 }} />
                <Row>
                  <Col span={24}>
                    <Text fontSize={16}>{t('¿Ya eres usuario de jobs2live?')}</Text>{' '}
                    <Link onClick={() => auth.signinRedirect()} disabled={isLoading} color="#1A1C21" fontSize={16} underline>
                      {t('Inicia sesión')}
                    </Link>
                  </Col>
                </Row>
              </>
            )}
          </Card>
        </Spin>
      </div>
    </div>
  );
};

export default IndexView;
